import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Coins, Zap, Loader2 } from 'lucide-react';
import { doc, updateDoc } from 'firebase/firestore';
import * as THREE from 'three';
import gsap from 'gsap';
import { db } from '../../services/firebase';

const ENTRY_FEE = 10;
const PAYOUT = 30; // 3x Payout for guessing right

// ============================================================================
// 1. THREE.JS ENGINE (Red Felt, No Particles, Bright Casino Lighting)
// ============================================================================
class ShellGameEngine {
    container: HTMLDivElement;
    callbacks: any;
    gameState: 'IDLE' | 'SHOWING' | 'SHUFFLING' | 'WAITING' | 'REVEALING';
    ballTargetIndex: number;
    cups: THREE.Group[];
    cameraNoise: number;
    scene: THREE.Scene | null = null;
    camera: THREE.PerspectiveCamera | null = null;
    renderer: THREE.WebGLRenderer | null = null;
    ball: THREE.Mesh | null = null;
    table: THREE.Mesh | null = null;
    animationFrameId: number = 0;
    raycaster: THREE.Raycaster;
    mouse: THREE.Vector2;

    CUP_POSITIONS = [-4.5, 0, 4.5];
    CUP_Y = 0.01; 
    BALL_Y = 0.42;

    constructor(container: HTMLDivElement, callbacks: any) {
        this.container = container;
        this.callbacks = callbacks;
        this.gameState = 'IDLE'; 
        this.ballTargetIndex = 1;
        this.cups = [];
        this.cameraNoise = 0;
        this.raycaster = new THREE.Raycaster();
        this.mouse = new THREE.Vector2();
        
        this.onResize = this.onResize.bind(this);
        this.onMouseDown = this.onMouseDown.bind(this);
        this.animate = this.animate.bind(this);
        
        this.init();
    }

    init() {
        const width = this.container.clientWidth;
        const height = this.container.clientHeight;

        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color(0x110000); // dark red-black background
        this.scene.fog = new THREE.Fog(0x110000, 15, 45);

        const initialFov = width < height ? 55 : 35;
        this.camera = new THREE.PerspectiveCamera(initialFov, width / height, 0.1, 1000);
        this.camera.position.set(0, 15, 28); 
        this.camera.lookAt(0, 2, 0);         

        this.renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: "high-performance", alpha: true });
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        this.renderer.setSize(width, height);
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
        this.renderer.toneMappingExposure = 1.5;
        this.container.appendChild(this.renderer.domElement);

        // --- Bright, warm casino lighting ---
        this.scene.add(new THREE.AmbientLight(0xffccaa, 0.65));

        const mainSpot = new THREE.SpotLight(0xffeedd, 10);
        mainSpot.position.set(0, 25, 18);
        mainSpot.angle = Math.PI / 5;
        mainSpot.penumbra = 0.5;
        mainSpot.decay = 1.5;
        mainSpot.castShadow = true;
        mainSpot.shadow.mapSize.set(2048, 2048);
        this.scene.add(mainSpot);

        const fillLight = new THREE.PointLight(0xff6644, 5, 30);
        fillLight.position.set(-8, 6, 8);
        this.scene.add(fillLight);

        const rimLight = new THREE.PointLight(0x00ccff, 3.5, 30);
        rimLight.position.set(8, 5, -12);
        this.scene.add(rimLight);

        this.createEnvironment();
        this.createGameObjects();
        // No particles

        window.addEventListener('resize', this.onResize);
        this.container.addEventListener('mousedown', this.onMouseDown);
        this.container.addEventListener('touchstart', this.onMouseDown, { passive: false });
        
        this.animationFrameId = requestAnimationFrame(this.animate);
    }

    createEnvironment() {
        if (!this.scene) return;
        
        // --- Bright red felt table (casino style) ---
        const feltCanvas = document.createElement('canvas');
        feltCanvas.width = 1024;
        feltCanvas.height = 1024;
        const fctx = feltCanvas.getContext('2d')!;
        fctx.fillStyle = '#b22222'; // firebrick red base
        fctx.fillRect(0, 0, 1024, 1024);
        // Add subtle texture
        for (let i = 0; i < 80000; i++) {
            const lum = Math.random() * 35;
            fctx.fillStyle = `rgba(200, 50, 50, ${Math.random() * 0.2})`;
            fctx.fillRect(Math.random() * 1024, Math.random() * 1024, 1, 1);
        }
        const feltTex = new THREE.CanvasTexture(feltCanvas);
        feltTex.wrapS = feltTex.wrapT = THREE.RepeatWrapping;
        feltTex.repeat.set(5, 5);

        const tableGeo = new THREE.CylinderGeometry(13, 13.5, 1.8, 96);
        const tableMat = new THREE.MeshPhysicalMaterial({
            map: feltTex,
            color: 0xcc3333,
            roughness: 0.85,
            metalness: 0.02,
            clearcoat: 0.1,
            sheen: 0xaa2222
        });
        this.table = new THREE.Mesh(tableGeo, tableMat);
        this.table.position.y = -1.0;
        this.table.receiveShadow = true;
        this.scene.add(this.table);

        // --- Glowing neon rim (cyan accent) ---
        const ringGeo = new THREE.TorusGeometry(13.1, 0.2, 16, 120);
        const ringMat = new THREE.MeshStandardMaterial({
            color: 0x00ccff,
            emissive: 0x00aaff,
            emissiveIntensity: 8
        });
        const ring = new THREE.Mesh(ringGeo, ringMat);
        ring.rotation.x = Math.PI / 2;
        ring.position.y = 0.05;
        this.scene.add(ring);

        // --- Dark reflective floor (adds depth without distracting) ---
        const floorGeo = new THREE.PlaneGeometry(300, 300);
        const floorMat = new THREE.MeshPhysicalMaterial({
            color: 0x0a0505,
            roughness: 0.2,
            metalness: 0.9,
            reflectivity: 0.8
        });
        const floor = new THREE.Mesh(floorGeo, floorMat);
        floor.rotation.x = -Math.PI / 2;
        floor.position.y = -1.8;
        floor.receiveShadow = true;
        this.scene.add(floor);
    }

    createGameObjects() {
        if (!this.scene) return;
        
        // --- Glowing Orb (cyan) ---
        const ballGeo = new THREE.IcosahedronGeometry(0.42, 4);
        const ballMat = new THREE.MeshPhysicalMaterial({
            color: 0x00ffff,
            emissive: 0x00aaff,
            emissiveIntensity: 14,
            roughness: 0.0,
            metalness: 0.9,
            transmission: 0.7,
            thickness: 0.8,
            clearcoat: 1.0
        });
        this.ball = new THREE.Mesh(ballGeo, ballMat);
        this.ball.castShadow = true;
        this.ball.position.set(this.CUP_POSITIONS[this.ballTargetIndex], this.BALL_Y, 0);
        this.scene.add(this.ball);

        // --- Cup geometry (elegant) ---
        const points: THREE.Vector2[] = [];
        const CUP_HEIGHT = 2.8;
        points.push(new THREE.Vector2(0, CUP_HEIGHT));
        points.push(new THREE.Vector2(0.85, CUP_HEIGHT));
        for (let i = 1; i <= 10; i++) {
            const a = (i / 10) * (Math.PI / 2);
            const x = 0.85 + 0.25 * Math.sin(a);
            const y = CUP_HEIGHT - 0.25 * (1 - Math.cos(a));
            points.push(new THREE.Vector2(x, y));
        }
        points.push(new THREE.Vector2(1.35, 0.4));
        for (let i = 1; i <= 10; i++) {
            const a = (i / 10) * (Math.PI / 2);
            const x = 1.35 + 0.15 * Math.sin(a);
            const y = 0.4 - 0.4 * Math.sin(a);
            points.push(new THREE.Vector2(x, y));
        }
        points.push(new THREE.Vector2(1.5, 0));

        const cupGeo = new THREE.LatheGeometry(points, 64);
        const cupMat = new THREE.MeshPhysicalMaterial({
            color: 0x0022aa,
            roughness: 0.01,
            metalness: 1.0,
            side: THREE.DoubleSide,
            clearcoat: 1.0,
            clearcoatRoughness: 0.0,
            reflectivity: 1.0
        });

        for (let i = 0; i < 3; i++) {
            const cupGroup = new THREE.Group();
            const body = new THREE.Mesh(cupGeo, cupMat);
            body.castShadow = true;
            body.receiveShadow = true;
            cupGroup.add(body);
            
            // Glowing base ring
            const baseRingGeo = new THREE.TorusGeometry(1.5, 0.06, 16, 80);
            const baseRingMat = new THREE.MeshBasicMaterial({ color: 0x00ccff });
            const baseRing = new THREE.Mesh(baseRingGeo, baseRingMat);
            baseRing.rotation.x = Math.PI / 2;
            baseRing.position.y = 0.06;
            cupGroup.add(baseRing);

            cupGroup.position.set(this.CUP_POSITIONS[i], this.CUP_Y, 0);
            this.scene.add(cupGroup);
            this.cups.push(cupGroup);
        }

        if (this.ball) this.cups[this.ballTargetIndex].attach(this.ball);
    }

    startGame() {
        if (this.gameState !== 'IDLE' && this.gameState !== 'REVEALING') return;
        this.gameState = 'SHOWING';
        this.callbacks.onStateChange("FOCUS ON THE ORB", false);
        
        if (this.ball) {
            this.scene?.attach(this.ball);
            this.ball.position.set(this.CUP_POSITIONS[this.ballTargetIndex], this.BALL_Y, 0);
        }
        
        this.ballTargetIndex = Math.floor(Math.random() * 3);
        
        const timeline = gsap.timeline();
        
        this.cups.forEach(cup => timeline.to(cup.position, { y: 6, duration: 0.5, ease: "power2.out" }, 0));
        
        if (this.ball) {
            timeline.to(this.ball.position, { x: this.CUP_POSITIONS[this.ballTargetIndex], duration: 0.5, ease: "power2.inOut" }, 0.5);
        }
        
        this.cups.forEach((cup, idx) => {
            timeline.to(cup.position, { y: this.CUP_Y, duration: 0.6, ease: "bounce.out" }, 1.0 + (idx * 0.1));
        });

        timeline.call(() => {
            if (this.ball) this.cups[this.ballTargetIndex].attach(this.ball);
            this.startShuffle();
        });
    }

    async startShuffle() {
        this.gameState = 'SHUFFLING';
        this.callbacks.onStateChange("SHUFFLING...", false);

        const shuffleCount = 20 + Math.floor(Math.random() * 10);
        const baseSpeed = 0.15;
        this.cameraNoise = 5;

        for (let i = 0; i < shuffleCount; i++) {
            await this.performSwap(baseSpeed * (0.8 + Math.random() * 0.4));
        }

        this.finishShuffle();
    }

    performSwap(speed: number) {
        return new Promise(resolve => {
            let idx1 = Math.floor(Math.random() * 3);
            let idx2;
            do { idx2 = Math.floor(Math.random() * 3); } while (idx1 === idx2);

            const cup1 = this.cups[idx1];
            const cup2 = this.cups[idx2];
            const pos1 = new THREE.Vector3().copy(cup1.position);
            const pos2 = new THREE.Vector3().copy(cup2.position);
            const dist = Math.abs(pos1.x - pos2.x);
            const zOffset = (Math.random() > 0.5 ? 1 : -1) * (dist / 1.2);

            gsap.to(cup1.position, { x: pos2.x, z: zOffset, duration: speed, ease: "power2.inOut" });
            gsap.to(cup2.position, {
                x: pos1.x, z: -zOffset, duration: speed, ease: "power2.inOut",
                onComplete: () => {
                    gsap.to([cup1.position, cup2.position], { z: 0, duration: speed * 0.2, onComplete: resolve });
                }
            });

            const temp = this.cups[idx1];
            this.cups[idx1] = this.cups[idx2];
            this.cups[idx2] = temp;
            
            if (this.ballTargetIndex === idx1) this.ballTargetIndex = idx2;
            else if (this.ballTargetIndex === idx2) this.ballTargetIndex = idx1;
        });
    }

    finishShuffle() {
        this.gameState = 'WAITING';
        this.cameraNoise = 0;
        this.callbacks.onStateChange("WHERE IS THE ORB?", false);
    }

    onMouseDown(event: MouseEvent | TouchEvent) {
        if (this.gameState !== 'WAITING' || !this.camera) return;

        const clientX = (event as TouchEvent).touches ? (event as TouchEvent).touches[0].clientX : (event as MouseEvent).clientX;
        const clientY = (event as TouchEvent).touches ? (event as TouchEvent).touches[0].clientY : (event as MouseEvent).clientY;

        const bounds = this.container.getBoundingClientRect();
        this.mouse.x = ((clientX - bounds.left) / bounds.width) * 2 - 1;
        this.mouse.y = -((clientY - bounds.top) / bounds.height) * 2 + 1;

        this.raycaster.setFromCamera(this.mouse, this.camera);
        const intersects = this.raycaster.intersectObjects(this.cups, true);

        if (intersects.length > 0) {
            let clickedCup: any = intersects[0].object;
            while (clickedCup.parent && !this.cups.includes(clickedCup)) clickedCup = clickedCup.parent;
            if (this.cups.includes(clickedCup)) this.reveal(this.cups.indexOf(clickedCup));
        }
    }

    reveal(selectedIndex: number) {
        this.gameState = 'REVEALING';
        const isCorrect = (selectedIndex === this.ballTargetIndex);
        
        if (this.ball) this.scene?.attach(this.ball);
        
        this.callbacks.onRevealResult(isCorrect);
        
        if (isCorrect) {
            this.callbacks.onStateChange("WINNER!", true, "CONTINUE");
        } else {
            this.callbacks.onStateChange("EMPTY. YOU LOST.", true, "TRY AGAIN");
        }

        this.cups.forEach((cup, idx) => {
            gsap.to(cup.position, { y: 6, duration: 0.8, ease: "expo.out", delay: idx === selectedIndex ? 0 : 0.2 });
        });
    }

    onResize() {
        if (!this.camera || !this.renderer) return;
        const width = this.container.clientWidth;
        const height = this.container.clientHeight;
        this.camera.aspect = width / height;
        
        if (width < height) {
            this.camera.fov = 55;
        } else {
            this.camera.fov = 35;
        }
        
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(width, height);
    }

    animate() {
        this.animationFrameId = requestAnimationFrame(this.animate);
        if (!this.scene || !this.camera || !this.renderer) return;

        const time = Date.now() * 0.001;
        const shakeX = (Math.random() - 0.5) * this.cameraNoise * 0.05;
        const shakeY = (Math.random() - 0.5) * this.cameraNoise * 0.05;
        
        // Gentle camera sway (casino vibe)
        this.camera.position.x = Math.sin(time * 0.4) * 5.5 + shakeX;
        this.camera.position.z = 28 + Math.cos(time * 0.2) * 2.5 + shakeY;
        this.camera.lookAt(0, 2, 0);
        
        if (this.ball) {
            this.ball.rotation.y += 0.05;
            this.ball.material.emissiveIntensity = 8 + Math.sin(time * 20) * 4;
        }
        
        this.renderer.render(this.scene, this.camera);
    }

    cleanup() {
        if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);
        window.removeEventListener('resize', this.onResize);
        if (this.container) {
            this.container.removeEventListener('mousedown', this.onMouseDown);
            this.container.removeEventListener('touchstart', this.onMouseDown);
        }
        if (this.renderer) {
            this.renderer.dispose();
        }
        this.scene = null;
        this.camera = null;
        this.renderer = null;
        this.ball = null;
        this.cups = [];
    }
}

// ============================================================================
// 2. REACT COMPONENT (Original layout, no scanline, no extra stats)
// ============================================================================
export const HypeShellsModal = ({ isOpen, onClose, profile, updateProfile, onBuyCredits }: any) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const engineRef = useRef<ShellGameEngine | null>(null);

    const [localBalance, setLocalBalance] = useState(0); 
    const [uiState, setUiState] = useState({
        statusMsg: "PLACE STAKE",
        btnVisible: true,
        btnText: "START DEAL",
        isWin: null as boolean | null
    });
    const [isProcessing, setIsProcessing] = useState(false);

    const refs = useRef({ localBalance, profile, updateProfile });
    
    useEffect(() => {
        if (profile?.cred !== undefined) setLocalBalance(profile.cred);
    }, [profile?.cred]);

    useEffect(() => {
        refs.current = { localBalance, profile, updateProfile };
    }, [localBalance, profile, updateProfile]);

    useEffect(() => {
        if (isOpen) document.body.style.overflow = 'hidden';
        else document.body.style.overflow = '';
        return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && containerRef.current && !engineRef.current) {
            engineRef.current = new ShellGameEngine(containerRef.current, {
                onStateChange: (statusMsg: string, btnVisible: boolean, btnText = "START DEAL") => {
                    setUiState(prev => ({ ...prev, statusMsg, btnVisible, btnText }));
                },
                onRevealResult: async (isWin: boolean) => {
                    setUiState(prev => ({ ...prev, isWin }));
                    if (isWin) {
                        const { localBalance: currentBal, profile: currentProf, updateProfile: currentUpdate } = refs.current;
                        const newBal = currentBal + PAYOUT;
                        setLocalBalance(newBal);
                        
                        if (currentUpdate) currentUpdate({ cred: newBal });
                        try {
                            if (currentProf?.uid) {
                                await updateDoc(doc(db, 'users', currentProf.uid), { cred: newBal });
                            }
                        } catch(e) { console.error(e); }
                    }
                    setIsProcessing(false);
                }
            });
        }

        return () => {
            if (engineRef.current) {
                engineRef.current.cleanup();
                engineRef.current = null;
            }
        };
    }, [isOpen]);

    const handleStart = async () => {
        if (localBalance < ENTRY_FEE) {
            if (onBuyCredits) onBuyCredits();
            return;
        }

        setIsProcessing(true);
        setUiState(prev => ({ ...prev, isWin: null }));

        const newBal = localBalance - ENTRY_FEE;
        setLocalBalance(newBal);
        
        if (updateProfile) updateProfile({ cred: newBal });
        try {
            await updateDoc(doc(db, 'users', profile.uid), { cred: newBal });
        } catch(e) { console.error(e); }

        if (engineRef.current) engineRef.current.startGame();
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[99999] bg-black font-sans text-white select-none h-[100dvh] w-[100vw] flex flex-col overflow-hidden m-0 p-0">
            
            {/* HEADER - Original layout */}
            <div className="shrink-0 p-4 md:p-6 flex justify-between items-start z-30 w-full pointer-events-auto">
                <div className="bg-black/60 backdrop-blur-md border border-cyan-500/30 p-2 md:p-4 rounded-xl flex items-center gap-2 md:gap-3">
                    <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-cyan-500/20 flex items-center justify-center shrink-0">
                        <Zap size={14} className="text-cyan-400" />
                    </div>
                    <div>
                        <h1 className="text-xs md:text-base font-black text-cyan-400 uppercase tracking-widest italic leading-none">Hype Shells</h1>
                        <p className="text-[8px] md:text-[9px] text-zinc-400 uppercase tracking-[0.2em] mt-1">Pick the Orb</p>
                    </div>
                </div>
                
                <div className="flex flex-col items-end gap-2 md:flex-row md:items-center">
                    <div className="bg-black/60 backdrop-blur-md border border-amber-500/30 p-2 md:p-3 rounded-xl text-right min-w-[100px] shadow-[0_0_20px_rgba(251,191,36,0.1)]">
                        <div className="text-[8px] md:text-[9px] text-zinc-400 uppercase tracking-widest mb-1 flex justify-end items-center gap-1"><Coins size={10}/> Bankroll</div>
                        <div className="text-lg md:text-2xl font-black text-white leading-none">
                            {localBalance} <span className="text-amber-400 text-xs md:text-sm">CRD</span>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 md:p-3 bg-zinc-900 border border-zinc-700 rounded-full hover:bg-white hover:text-black transition-colors shadow-xl">
                        <X size={18} />
                    </button>
                </div>
            </div>

            {/* MIDDLE AREA (Canvas + Status Text) */}
            <div className="flex-1 relative w-full flex flex-col justify-center">
                <div ref={containerRef} className="absolute inset-0 z-0" />
                
                <div className="relative z-20 text-center w-full px-4 pointer-events-none mt-[-20vh]">
                    <div className={`text-2xl md:text-5xl uppercase font-black tracking-widest italic transition-all duration-300 ${uiState.isWin === true ? 'text-emerald-400 drop-shadow-[0_0_30px_rgba(16,185,129,0.8)] scale-110' : uiState.isWin === false ? 'text-red-500 drop-shadow-[0_0_30px_rgba(239,68,68,0.8)]' : 'text-white drop-shadow-lg'}`}>
                        {uiState.statusMsg}
                    </div>
                </div>
            </div>
            
            {/* FOOTER BUTTON - Original */}
            <div className="shrink-0 p-4 md:p-8 flex justify-center z-30 pb-safe w-full pointer-events-auto">
                <button 
                    onClick={handleStart}
                    disabled={!uiState.btnVisible || isProcessing}
                    className={`w-full max-w-sm px-6 py-5 md:py-6 rounded-2xl text-xs md:text-sm font-black tracking-[0.3em] uppercase transition-all duration-300 shadow-[0_0_30px_rgba(0,204,255,0.2)] ${uiState.btnVisible && !isProcessing ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white hover:scale-105 active:scale-95' : 'opacity-0 translate-y-10 pointer-events-none'}`}
                >
                    {isProcessing ? <Loader2 className="animate-spin mx-auto" /> : (
                        <span className="flex flex-col items-center">
                            <span>{uiState.btnText}</span>
                            <span className="text-[9px] text-cyan-200 mt-1">Cost: {ENTRY_FEE} CRD | Payout: {PAYOUT} CRD</span>
                        </span>
                    )}
                </button>
            </div>

        </div>,
        document.body 
    );
};